import { createSelector } from '@reduxjs/toolkit';
import { PlannedMeal } from '../mealPlanSlice';
import { isEntryMeal } from '../mealEntry';
import { todayIso } from '../../utils/localDate';
import { getProteinType } from '../../utils/proteinType';

export interface WeekProgressSummary {
  cooked: number;
  skipped: number;   // past + not cooked
  upcoming: number;  // today or future + not cooked
  total: number;
  byProtein: {
    meat:   { cooked: number; planned: number };
    fish:   { cooked: number; planned: number };
    veggie: { cooked: number; planned: number };
  };
}

interface RootStateLike {
  mealPlan: {
    weeks: Record<string, PlannedMeal[]>;
  };
}

const selectWeekMeals = (state: RootStateLike, weekStartDate: string): PlannedMeal[] =>
  state.mealPlan.weeks[weekStartDate] || [];

function buildSummary(meals: PlannedMeal[], today: string): WeekProgressSummary {
  const summary: WeekProgressSummary = {
    cooked: 0,
    skipped: 0,
    upcoming: 0,
    total: 0,
    byProtein: {
      meat:   { cooked: 0, planned: 0 },
      fish:   { cooked: 0, planned: 0 },
      veggie: { cooked: 0, planned: 0 },
    },
  };

  for (const m of meals) {
    // Skip non-recipe entries (dining, notes, skip, etc.) and truly empty slots.
    // Count recipe meals whether or not the recipe object has been rehydrated yet.
    if (isEntryMeal(m) || !m.recipeId) continue;

    summary.total += 1;

    const isCooked = m.status === 'cooked';
    if (isCooked) {
      summary.cooked += 1;
    } else if (m.scheduledFor < today) {
      summary.skipped += 1;
    } else {
      summary.upcoming += 1;
    }

    const protein = getProteinType(m);
    if (protein === 'meat' || protein === 'fish' || protein === 'veggie') {
      summary.byProtein[protein].planned += 1;
      if (isCooked) summary.byProtein[protein].cooked += 1;
    }
  }

  return summary;
}


export const selectWeekProgressSummary = createSelector(
  [
    (state: RootStateLike, weekStartDate: string) => selectWeekMeals(state, weekStartDate),
    (_state: RootStateLike, _weekStartDate: string, today?: string) => today ?? todayIso(),
  ],
  (meals, today) => buildSummary(meals, today),
);
